import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { truncate } from '@/seo/config';
import { postJsonLd } from '@/seo/jsonld';
import ReactionBar from '@/components/ReactionBar';
import ShareButton from '@/components/ShareButton';
import { Button } from '@/components/ui/button';
import { Page, PageContent, PageHeader, SectionTitle } from '@/components/layout';
import { CategoryBadge, PostCardSkeleton } from '@/components/PostCard';
import PollDisplay from '@/components/PollDisplay';
import ReportButton from '@/components/ReportButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Heart, MapPin, Calendar, MessageCircle, Send, Trash2, Play, Pause, BadgeCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const PostDetailPage = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user, api } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const [postResponse, commentsResponse] = await Promise.all([
          api.get(`/posts/${postId}`),
          api.get(`/posts/${postId}/comments`)
        ]);
        setPost(postResponse.data);
        setComments(commentsResponse.data);
      } catch (error) {
        toast.error('Post non trouvé');
        navigate('/feed');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, api, navigate]);

  const handleLike = async () => {
    if (!user) {
      toast.error('Connectez-vous pour liker');
      return;
    }

    try {
      const response = await api.post(`/posts/${postId}/like`);
      setPost({ 
        ...post, 
        is_liked: response.data.liked, 
        likes_count: response.data.likes_count 
      });
    } catch (error) {
      toast.error('Erreur lors du like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Connectez-vous pour commenter');
      return;
    }

    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/posts/${postId}/comments`, {
        content: newComment
      });
      setComments([response.data, ...comments]);
      setPost({ ...post, comments_count: post.comments_count + 1 });
      setNewComment('');
      toast.success('Commentaire ajouté !');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce post ?')) return;

    try {
      await api.delete(`/posts/${postId}`);
      toast.success('Post supprimé');
      navigate('/feed');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Page nav={!!user}>
        <PageContent>
          <PostCardSkeleton />
        </PageContent>
      </Page>
    );
  }

  if (!post) return null;

  const isOwner = user?.user_id === post.user_id;

  return (
    <Page nav={!!user} testId="post-detail-page">
      <SEO
        title={`${post.title} – ${post.commune} | Bourbon`}
        description={truncate(post.content)}
        path={`/post/${post.post_id}`}
        image={post.image_url || undefined}
        type="article"
        publishedTime={post.created_at}
        jsonLd={postJsonLd(post)}
      />
      <PageHeader
        back
        backTo={user ? undefined : '/'}
        title="Publication"
        subtitle={post.commune}
        actions={isOwner && (
          <button onClick={handleDelete} className="h-10 w-10 rounded-full inline-flex items-center justify-center text-destructive hover:bg-destructive/10 transition-colors" aria-label="Supprimer" data-testid="delete-btn">
            <Trash2 className="h-5 w-5" />
          </button>
        )}
      />

      <PageContent>
        <article className="post-card animate-fade-in">
          {post.image_url && post.image_url.trim() !== '' && (
            <div className="relative bg-muted">
              <img src={post.image_url} alt={post.title} className="w-full max-h-[70vh] object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              <div className="absolute top-3 left-3"><CategoryBadge category={post.category} className="glass !bg-background/70" /></div>
            </div>
          )}
          <div className="p-5 sm:p-7">
            <div className="flex items-start justify-between gap-3 mb-5">
              <button className="flex items-center gap-3 text-left" onClick={() => navigate(`/profile/${post.user_id}`)} data-testid="post-author-link">
                <Avatar className="h-12 w-12 ring-2 ring-background shadow">
                  <AvatarImage src={post.user_picture} />
                  <AvatarFallback className="bg-secondary/15 text-secondary font-bold">{post.user_name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold leading-tight flex items-center gap-1">
                    {post.user_name}
                    {post.is_verified_commerce && <BadgeCheck className="h-4 w-4 text-secondary" />}
                  </p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {post.commune}{post.quartier ? ` · ${post.quartier}` : ''} · {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: fr })}
                  </p>
                </div>
              </button>
              {!post.image_url && <CategoryBadge category={post.category} />}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold leading-tight mb-4" data-testid="post-title">{post.title}</h1>
            <p className="text-[16px] leading-relaxed text-foreground/85 whitespace-pre-wrap mb-6" data-testid="post-content">{post.content}</p>

            {post.audio_url && post.audio_url.trim() !== '' && (
              <div className="flex items-center gap-3 p-4 bg-muted/60 rounded-xl mb-6">
                <button
                  onClick={() => {
                    if (isPlaying) { audioRef.current?.pause(); setIsPlaying(false); }
                    else { audioRef.current?.play(); setIsPlaying(true); }
                  }}
                  className="h-12 w-12 rounded-full lagoon-gradient text-white inline-flex items-center justify-center shadow btn-bounce"
                  data-testid="audio-play-btn"
                >
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                </button>
                <div className="flex-1">
                  <p className="text-sm font-semibold mb-1">Message audio</p>
                  <audio ref={audioRef} src={post.audio_url} onEnded={() => setIsPlaying(false)} controls className="w-full h-8" />
                </div>
              </div>
            )}

            {post.event_date && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 text-amber-800 dark:text-amber-200 mb-6" data-testid="post-event-date">
                <span className="h-10 w-10 rounded-lg bg-amber-500 text-white inline-flex items-center justify-center"><Calendar className="h-5 w-5" /></span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider opacity-70">Événement</p>
                  <p className="font-semibold">{new Date(post.event_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            )}

            {post.poll_options && post.poll_options.length > 0 && (
              <div className="mb-6"><PollDisplay post={post} onVote={() => api.get(`/posts/${postId}`).then((r) => setPost(r.data))} /></div>
            )}

            <div className="flex flex-wrap items-center gap-1 sm:gap-2 pt-4 border-t border-border/60">
              <button
                onClick={handleLike}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors hover:bg-muted ${post.is_liked ? 'text-primary' : ''}`}
                data-testid="like-btn"
              >
                <Heart className={`h-5 w-5 ${post.is_liked ? 'fill-current' : ''}`} />
                {post.likes_count}
              </button>
              <ReactionBar post={post} size="lg" onChange={(fields) => setPost((p) => ({ ...p, ...fields }))} />
              <span className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground">
                <MessageCircle className="h-5 w-5" />
                {post.comments_count}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <ShareButton
                  post={post}
                  onShare={async () => {
                    try {
                      const { data } = await api.post(`/posts/${post.post_id}/share`);
                      setPost((p) => ({ ...p, shares_count: data.shares_count }));
                    } catch (error) {
                      console.error('Share error:', error);
                    }
                  }}
                />
                <ReportButton postId={post.post_id} type="post" />
              </div>
            </div>
          </div>
        </article>

        <section className="mt-6" data-testid="comments-section">
          <SectionTitle eyebrow="Discussion">
            {post.comments_count} commentaire{post.comments_count > 1 ? 's' : ''}
          </SectionTitle>

          {user ? (
            <form onSubmit={handleComment} className="surface p-3 flex items-end gap-3 mb-5">
              <Avatar className="h-9 w-9 hidden sm:block">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="bg-primary/15 text-primary font-bold">{user.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <Textarea
                placeholder="Ajouter un commentaire..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[44px] max-h-40 resize-none border-0 bg-transparent focus-visible:ring-0 px-1"
                rows={1}
                data-testid="comment-input"
              />
              <Button type="submit" disabled={submitting || !newComment.trim()} className="rounded-xl h-10 w-10 p-0 shrink-0" data-testid="submit-comment-btn" aria-label="Envoyer">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          ) : (
            <div className="surface p-5 text-center mb-5">
              <p className="text-sm text-muted-foreground mb-3">Connectez-vous pour rejoindre la discussion</p>
              <Button onClick={() => navigate('/auth')} className="rounded-xl" data-testid="login-to-comment-btn">Se connecter</Button>
            </div>
          )}

          {comments.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">Aucun commentaire. Lancez la conversation !</p>
          ) : (
            <div className="space-y-3 stagger-children">
              {comments.map((comment) => (
                <div key={comment.comment_id} className="flex gap-3" data-testid={`comment-${comment.comment_id}`}>
                  <Avatar className="h-9 w-9 mt-1 cursor-pointer" onClick={() => navigate(`/profile/${comment.user_id}`)}>
                    <AvatarImage src={comment.user_picture} />
                    <AvatarFallback className="text-xs bg-muted font-bold">{comment.user_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="surface rounded-2xl rounded-tl-md px-4 py-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-semibold text-sm truncate">{comment.user_name}</span>
                        <span className="text-[11px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: fr })}</span>
                      </div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{comment.content}</p>
                    </div>
                    
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </PageContent>
    </Page>
  );
};

export default PostDetailPage;
